import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	selectProductInputs,
	setProductInputs,
} from "../../../../features/current-loan/currentLoanSlice";
import { selectQuickPricerFormData } from "../../../../features/quickPricer/quickPricerFormSlice";
import { selectIncomeDocumentationType } from "../../../../features/current-loan/currentLoanSlice";
import { PRODUCT_INPUTS_FORM_CONFIG } from "./productInputs.form.config";
import { intersectFieldErrors } from "../../../../utils/required-field-helper/product-inputs-required-helper";
import {
	PRODUCT_INPUTS_DEFAULTS,
	QUICK_PRICER_FIELD_MAP,
	QUICK_PRICER_SEED_SKIP,
	isOccupancyOption,
	isPropertyTypeOption,
	toOccupancyOption,
	toPropertyTypeOption,
} from "../../../../utils/aura.constants";

const isEmptyValue = (val) =>
	val === "" || val === null || val === undefined || (Array.isArray(val) && val.length === 0);

// Shared by the AURA and Without-AURA paths. Values written during AURA (e.g. a credit re-issue
// FICO) stay on the loan, so the Without-AURA path shows them too (AURA Run Process rev. 10/21).
export const useProductInputsForm = ({ displayData, creditResult } = {}) => {
	const dispatch = useDispatch();
	const formState = useSelector(selectProductInputs);
	const quickPricerData = useSelector(selectQuickPricerFormData);
	const incomeDocumentationType = useSelector(selectIncomeDocumentationType);
	const [errors, setErrors] = useState({});
	const [submitted, setSubmitted] = useState(false);

	const setField = (name) => (value) => {
		dispatch(setProductInputs({ [name]: value }));
	};

	// Merges a fetch-on-open reverse-mapped Encompass response over the current form
	// state — Encompass's value wins when present, everything else is left untouched
	// (the reverse-mapper itself only ever includes keys it found a real value for).
	const hydrateFromEncompass = (partial) => {
		if (partial && Object.keys(partial).length > 0) {
			dispatch(setProductInputs(partial));
		}
	};

	// Pre-seed fields from XML data (only once when the data first arrives)
	useEffect(() => {
		if (!displayData) return;
		const seeds = {};

		if (displayData.loanAmountRaw && !formState.loanAmount)
			seeds.loanAmount = String(displayData.loanAmountRaw);

		if (displayData.appraisedValueRaw && !formState.estimatedValue)
			seeds.estimatedValue = String(displayData.appraisedValueRaw);

		if (displayData.loanPurpose && !formState.loanPurpose)
			seeds.loanPurpose = displayData.loanPurpose;

		// Display data carries labels ("Single Family") or Encompass/XML values; the select
		// needs an option value. Also replaces an earlier seed that matched no option.
		const seededPropertyType = toPropertyTypeOption(displayData.propertyType);
		if (seededPropertyType && !isPropertyTypeOption(formState.propertyType))
			seeds.propertyType = seededPropertyType;

		// Display data carries a label ("Investment Property"); the select needs the option.
		const seededOccupancy = toOccupancyOption(displayData.occupancy);
		if (seededOccupancy && !isOccupancyOption(formState.occupancy))
			seeds.occupancy = seededOccupancy;

		if (displayData.state && !formState.state) seeds.state = displayData.state;
		if (displayData.county && !formState.county) seeds.county = displayData.county;
		if (displayData.city && !formState.city) seeds.city = displayData.city;
		if (displayData.zip && !formState.zipcode) seeds.zipcode = displayData.zip;

		if (Object.keys(seeds).length > 0) dispatch(setProductInputs(seeds));
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [displayData]);

	// Pre-seed FICO and housing event from credit re-issue Step A result. A FICO from a new
	// re-issue replaces the one already in the form (a second re-issue used to keep the old
	// score); on first load it only fills an empty field.
	const lastCreditFicoRef = useRef(creditResult?.fico);
	useEffect(() => {
		if (!creditResult) return;
		const seeds = {};

		const fico = creditResult.fico && creditResult.fico !== "—" ? creditResult.fico : "";
		const isNewFico = fico !== lastCreditFicoRef.current;
		lastCreditFicoRef.current = fico;
		if (fico && (isNewFico || !formState.qualifyingCreditScore))
			seeds.qualifyingCreditScore = String(fico);

		if (creditResult.event && creditResult.event !== "—" && !formState.housingEventType)
			seeds.housingEventType = creditResult.event;

		if (Object.keys(seeds).length > 0) dispatch(setProductInputs(seeds));
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [creditResult]);

	// Pre-seed remaining empty fields from the borrower's previously-entered Quick Pricer values
	useEffect(() => {
		if (!quickPricerData) return;
		const seeds = {};

		Object.entries(quickPricerData).forEach(([qpKey, qpValue]) => {
			const piKey = QUICK_PRICER_FIELD_MAP[qpKey] || qpKey;
			if (QUICK_PRICER_SEED_SKIP.has(piKey)) return;
			if (!(piKey in PRODUCT_INPUTS_DEFAULTS)) return;
			if (isEmptyValue(qpValue) || !isEmptyValue(formState[piKey])) return;
			seeds[piKey] = qpValue;
		});

		if (Object.keys(seeds).length > 0) dispatch(setProductInputs(seeds));
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [quickPricerData]);

	// Income Verification Type is now read-only here — always mirror the Dashboard's
	// authoritative Income Documentation Type (currentLoanSlice.incomeDocumentationType)
	// rather than seeding once, since the field can no longer be edited independently.
	useEffect(() => {
		if (!incomeDocumentationType) return;
		if (formState.incomeVerificationType === incomeDocumentationType) return;
		dispatch(setProductInputs({ incomeVerificationType: incomeDocumentationType }));
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [incomeDocumentationType, formState.incomeVerificationType]);

	// Run computed fields (LTV/CLTV/HCLTV and occupancy auto-set)
	useEffect(() => {
		const computed = {};
		PRODUCT_INPUTS_FORM_CONFIG.sections.forEach((section) => {
			section.fields.forEach((field) => {
				if (field.computed && typeof field.compute === "function") {
					const next = field.compute(formState);
					if (formState[field.name] !== next) computed[field.name] = next;
				}
			});
		});
		if (Object.keys(computed).length > 0) dispatch(setProductInputs(computed));
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		formState.loanPurpose,
		formState.purchasePrice,
		formState.estimatedValue,
		formState.loanAmount,
		formState.closedEndSecondLienAmt,
		formState.helocDrawnAmount,
		formState.helocLineAmount,
		formState.cashoutAmount,
		formState.incomeVerificationType,
	]);

	const validateForm = () => {
		const newErrors = {};
		PRODUCT_INPUTS_FORM_CONFIG.sections.forEach((section) => {
			section.fields.forEach((field) => {
				if (!field.component) return;
				const isRequired =
					typeof field.required === "function"
						? field.required(formState)
						: field.required;
				const isVisible = field.visibleIf ? field.visibleIf(formState) : true;
				if (!isRequired || !isVisible) return;
				const value = formState[field.name];
				// A select whose saved value isn't in its current options shows blank, so it
				// counts as empty (e.g. Prepayment Penalty "None" once the field is required).
				const options = typeof field.options === "function" ? field.options(formState) : field.options;
				const notAnOption =
					field.component === "select" &&
					Array.isArray(options) &&
					!Array.isArray(value) &&
					value !== "" &&
					value !== null &&
					value !== undefined &&
					!options.some((o) => String(o?.value) === String(value));
				const isEmpty =
					value === "" ||
					value === null ||
					value === undefined ||
					(Array.isArray(value) && value.length === 0) ||
					notAnOption;
				if (isEmpty) newErrors[field.name] = `${field.label} is required`;
			});
		});
		return newErrors;
	};

	// Like the Dashboard: a field flagged on submit stops showing red as soon as it
	// has a value — only fields flagged on submit AND still invalid now are shown.
	const shownErrors = intersectFieldErrors(errors, validateForm());

	return {
		formState: formState || PRODUCT_INPUTS_DEFAULTS,
		setField,
		hydrateFromEncompass,
		errors: shownErrors,
		setErrors,
		submitted,
		setSubmitted,
		validateForm,
	};
};
